<template>
  <div class="hold-instructions-editor">
    <div class="mb-3">
      <label for="hold-percentage" class="col-form-label">{{
        t("hold_strategy.percentage_to_hold")
      }}</label>
      <input
        type="number"
        class="form-control"
        id="hold-percentage"
        :value="model.portfolio_instructions.allocate_percentage"
        @input="
          updateInstructionsField(
            'allocate_percentage',
            normalizePercentage(($event.target as HTMLInputElement).value)
          )
        "
        @blur="v$.allocate_percentage.$touch()"
        :class="{ 'is-invalid': v$.allocate_percentage.$error }"
      />
      <div v-if="v$.allocate_percentage.$error" class="invalid-feedback">
        <div v-for="error of v$.allocate_percentage.$errors" :key="error.$uid">
          {{ error.$message }}
        </div>
      </div>
    </div>

    <div class="mb-3">
      <div class="form-label fw-semibold">
        {{ t("portfolio_instructions.selected_devices") }}
      </div>
      <div class="form-check">
        <input
          class="form-check-input"
          type="radio"
          name="hold-device-scope"
          id="hold-scope-all"
          value="all"
          v-model="selectionMode"
          @change="setGlobalMode"
        />
        <label class="form-check-label" for="hold-scope-all">
          {{ t("hold_strategy.scope_all_label") }}
        </label>
        <div class="form-text">
          {{ t("hold_strategy.scope_all_description") }}
        </div>
      </div>
      <div class="form-check mt-2">
        <input
          class="form-check-input"
          type="radio"
          name="hold-device-scope"
          id="hold-scope-specific"
          value="specific"
          v-model="selectionMode"
        />
        <label class="form-check-label" for="hold-scope-specific">
          {{ t("hold_strategy.scope_specific_label") }}
        </label>
        <div class="form-text">
          {{ t("hold_strategy.scope_specific_description") }}
        </div>
      </div>
    </div>

    <div v-show="selectionMode === 'specific'" class="mt-3">
      <RegistryDeviceSelector
        :modelValue="model.portfolio_instructions.registry_device_ids ?? []"
        :registryDevices="props.registryDevices ?? []"
        :availableOptions="availableDeviceOptions"
        :placeholder="deviceSelectorPlaceholder"
        @update:modelValue="updateDeviceIds"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useValidators } from "@/composables/useValidators";
import { getDeviceName } from "@/dashboard/production_strategy/overview/PPA/ppaDeviceUtils";
import RegistryDeviceSelector from "@/dashboard/production_strategy/overview/RegistryDeviceSelector.vue";
import type { PortfolioWithInstructions, RegistryDevice } from "@/types";
import { useVuelidate } from "@vuelidate/core";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  registryDevices: RegistryDevice[];
}>();

const model = defineModel<PortfolioWithInstructions>({ required: true });

const { t } = useI18n();
const customValidators = useValidators(t);

interface DeviceOption {
  label: string;
  value: string;
  description?: string;
}

const updateInstructionsField = (
  field: keyof PortfolioWithInstructions["portfolio_instructions"],
  value: any
) => {
  model.value = {
    ...model.value,
    portfolio_instructions: {
      ...model.value.portfolio_instructions,
      [field]: value,
    },
  };
};

const rules = computed(() => ({
  allocate_percentage: {
    required: customValidators.required,
    between: customValidators.between(0, 100),
  },
}));

const v$ = useVuelidate(
  rules,
  computed(() => ({
    allocate_percentage: model.value.portfolio_instructions.allocate_percentage,
  })),
  {
    $autoDirty: true,
  }
);

const normalizePercentage = (value: string) => {
  const parsed = Number.parseFloat(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const selectionMode = ref<"all" | "specific">(
  (model.value.portfolio_instructions.registry_device_ids ?? []).length > 0
    ? "specific"
    : "all"
);

const availableDeviceOptions = computed<DeviceOption[]>(() =>
  (props.registryDevices ?? []).map((device) => ({
    label: getDeviceName(device.id.toString(), props.registryDevices ?? []),
    value: device.id.toString(),
    description: device.device_eic,
  }))
);

const deviceSelectorPlaceholder = computed(() =>
  t("portfolio_instructions.select_devices")
);

const setGlobalMode = () => {
  selectionMode.value = "all";
  model.value = {
    ...model.value,
    portfolio_instructions: {
      ...model.value.portfolio_instructions,
      registry_device_ids: [],
    },
  };
};

const updateDeviceIds = (deviceIds: string[]) => {
  selectionMode.value = "specific";
  model.value = {
    ...model.value,
    portfolio_instructions: {
      ...model.value.portfolio_instructions,
      registry_device_ids: deviceIds ?? [],
    },
  };
};

watch(
  () => model.value.portfolio_instructions.registry_device_ids,
  (ids) => {
    selectionMode.value = (ids ?? []).length > 0 ? "specific" : "all";
  }
);
</script>

<style scoped lang="scss">
.is-invalid + .invalid-feedback {
  display: block;
}
</style>
